import React, { useState, useEffect } from 'react';
import { 
  Firestore,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../../hooks/AuthProvider';
import LoadingPage from '../../LoadingPage';
import StoreManagement from '../StoreManagement/StoreManagement';
import './ControlPanel.css';

// Basic App interface
interface App {
  id: string;
  name: string;
  type?: string;
  region?: string;
  organizationId: string;
  appIcon?: string;
  status?: string;
}

// Store interface
interface Store {
  id?: string;
  name: string;
  city: string;
  country: string;
  stripeConnected: boolean;
}

// Deployment status interface
interface DeploymentStatus {
  projectId?: string;
  status?: string;
  details?: string;
  timestamp?: string;
  url?: string;
  currentStep?: string;
  requestId?: string;
  error?: string;
  updateAvailable?: boolean;
  theme?: {
    variant: string;
    accentColor: string | null;
    customTheme: any | null;
    hasCustomTheme: boolean;
  };
  appIconUrl?: string | null;
  menuOnly?: boolean;  // ADDED
}

// Theme interface
interface Theme {
  backgroundColor: string;
  textColor: string;
  cardColor: string;
  topbarBackgroundColor: string;
  topbarIconColor: string;
  topbarBorderBottomColor: string;
  sidebarBackgroundColor: string;
  sidebarIconColor: string;
  sidebarIconColorSelected: string;
  sidebarTextColor: string;
  sidebarBorderColor: string;
  borderColor: string;
  categoryIndicatorColor: string;
  cartBackgroundColor: string;
  cartCardColor: string;
  cartTextColor: string;
  cartButtonColor: string;
  cartButtonTextColor: string;
  cartSummaryBorder: string;
  payButtonColor: string;
  payButtonTextColor: string;
  loaderColor: string;
  [key: string]: string;
}

interface ControlPanelProps {
  db: Firestore;
  functions: Functions;
  appData: App;
  organizationId: string;
  onBack: () => void;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
}

const ControlPanel: React.FC<ControlPanelProps> = ({
  db,
  functions,
  appData,
  organizationId,
  onBack,
  onError,
  onSuccess,
  setLoading
}) => {
  const { currentUser } = useAuth();
  const [stores, setStores] = useState<Store[]>([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [showStoreManagement, setShowStoreManagement] = useState(false);
  const [deploymentStatus, setDeploymentStatus] = useState<DeploymentStatus | null>(null);
  const [deploymentLoading, setDeploymentLoading] = useState(false);
  const [retryingDeployment, setRetryingDeployment] = useState(false);

  // Theme update modal state
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [themeUpdateMode, setThemeUpdateMode] = useState<'default' | 'custom'>('default');
  const [selectedTheme, setSelectedTheme] = useState<'modern-light' | 'modern-dark'>('modern-light');
  const [accentColor, setAccentColor] = useState('#2563eb');
  const [customTheme, setCustomTheme] = useState<Theme | null>(null);
  const [customThemeError, setCustomThemeError] = useState('');
  const [customThemeFileName, setCustomThemeFileName] = useState('');
  const [updatingTheme, setUpdatingTheme] = useState(false);

  // Icon update modal state
  const [showIconModal, setShowIconModal] = useState(false);
  const [appIconUrl, setAppIconUrl] = useState('');
  const [iconPreviewUrl, setIconPreviewUrl] = useState('');
  const [updatingIcon, setUpdatingIcon] = useState(false);
  const [uploadingIconFile, setUploadingIconFile] = useState(false);

  // Menu Only modal state - ADDED
  const [showMenuOnlyModal, setShowMenuOnlyModal] = useState(false);
  const [menuOnlyValue, setMenuOnlyValue] = useState(false);
  const [updatingMenuOnly, setUpdatingMenuOnly] = useState(false);

  // Load stores for this app
  useEffect(() => {
    const loadStores = async () => {
      if (!appData?.id) {
        setStoresLoading(false);
        return;
      }

      setStoresLoading(true);
      try {
        const q = query(
          collection(db, 'stores'),
          where('appId', '==', appData.id)
        );
        const querySnapshot = await getDocs(q);
        
        const storesList: Store[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          storesList.push({
            id: doc.id,
            name: data.name || 'Untitled Store',
            city: data.city || 'Unknown City',
            country: data.country || 'Unknown Country',
            stripeConnected: data.stripeConnected || false
          });
        });

        setStores(storesList);
      } catch (err) {
        console.error('Error loading stores:', err);
        onError('Failed to load stores.');
      } finally {
        setStoresLoading(false);
      }
    };

    loadStores();
  }, [db, appData?.id, onError]);

  // Load deployment status
  useEffect(() => {
    if (appData?.id) {
      checkDeploymentStatus();
    }
  }, [appData?.id]);

  useEffect(() => {
    checkDeploymentStatus();
  }, [stores.length])

  const checkDeploymentStatus = async () => {
    if (!appData?.id) return;

    setDeploymentLoading(true);
    try {
      const checkStatus = httpsCallable(functions, 'checkInstanceDeploymentStatus');
      const result = await checkStatus({ appId: appData.id });
      console.log("deployment status: ", result)
      setDeploymentStatus(result.data as DeploymentStatus);
    } catch (err: any) {
      console.error('Error checking deployment status:', err);
      setDeploymentStatus(null);
    } finally {
      setDeploymentLoading(false);
    }
  };

  const handleRetryDeployment = async () => {
    if (!appData?.id) return;

    setRetryingDeployment(true);
    try {
      const retryDeployment = httpsCallable(functions, 'retryInstanceDeployment');
      await retryDeployment({ appId: appData.id });
      onSuccess('Deployment retry initiated successfully');
      
      setTimeout(() => {
        checkDeploymentStatus();
      }, 2000);
    } catch (err: any) {
      console.error('Error retrying deployment:', err);
      onError(err.message || 'Failed to retry deployment');
    } finally {
      setRetryingDeployment(false);
    }
  };

  const handleManageStores = () => {
    setShowStoreManagement(true);
  };

  const handleBackFromStoreManagement = () => {
    setShowStoreManagement(false);
    const reloadStores = async () => {
      if (!appData?.id) return;

      setStoresLoading(true);
      try {
        const q = query(
          collection(db, 'stores'),
          where('appId', '==', appData.id)
        );
        const querySnapshot = await getDocs(q);
        
        const storesList: Store[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          storesList.push({
            id: doc.id,
            name: data.name || 'Untitled Store',
            city: data.city || 'Unknown City',
            country: data.country || 'Unknown Country',
            stripeConnected: data.stripeConnected || false
          });
        });

        setStores(storesList);
      } catch (err) {
        console.error('Error reloading stores:', err);
        onError('Failed to reload stores.');
      } finally {
        setStoresLoading(false);
      }
    };

    reloadStores();
  };

  // Theme modal handlers
  const handleOpenThemeModal = () => {
    setShowThemeModal(true);
    
    // Prepopulate with current theme values from deployment status
    if (deploymentStatus?.theme) {
      if (deploymentStatus.theme.hasCustomTheme) {
        // Has custom theme
        setThemeUpdateMode('custom');
        setCustomTheme(deploymentStatus.theme.customTheme);
        setCustomThemeFileName('current-custom-theme.json');
      } else {
        // Has default theme
        setThemeUpdateMode('default');
        setSelectedTheme(deploymentStatus.theme.variant as 'modern-light' | 'modern-dark');
        setAccentColor(deploymentStatus.theme.accentColor || '#2563eb');
        setCustomTheme(null);
        setCustomThemeFileName('');
      }
    } else {
      // No theme info, use defaults
      setThemeUpdateMode('default');
      setSelectedTheme('modern-light');
      setAccentColor('#2563eb');
      setCustomTheme(null);
      setCustomThemeFileName('');
    }
    
    setCustomThemeError('');
  };

  const handleCloseThemeModal = () => {
    setShowThemeModal(false);
    setThemeUpdateMode('default');
    setCustomTheme(null);
    setCustomThemeError('');
    setCustomThemeFileName('');
  };

  // Icon modal handlers
  const handleOpenIconModal = () => {
    setShowIconModal(true);
    
    // Prepopulate with current icon URL from deployment status
    const currentIcon = deploymentStatus?.appIconUrl || '';
    setAppIconUrl(currentIcon);
    setIconPreviewUrl(currentIcon);
  };

  const handleCloseIconModal = () => {
    setShowIconModal(false);
    setAppIconUrl('');
    setIconPreviewUrl('');
  };

  // Menu Only modal handlers - ADDED
  const handleOpenMenuOnlyModal = () => {
    setShowMenuOnlyModal(true);
    
    // Prepopulate with current menu_only value from deployment status
    const currentMenuOnly = deploymentStatus?.menuOnly || false;
    setMenuOnlyValue(currentMenuOnly);
  };

  const handleCloseMenuOnlyModal = () => {
    setShowMenuOnlyModal(false);
    setMenuOnlyValue(false);
  };

  const handleSubmitMenuOnlyUpdate = async () => {
    if (!appData?.id) return;

    setUpdatingMenuOnly(true);
    try {
      const retryDeployment = httpsCallable(functions, 'retryInstanceDeployment');
      
      await retryDeployment({
        appId: appData.id,
        menuOnly: menuOnlyValue
      });
      
      onSuccess(`Menu Only mode ${menuOnlyValue ? 'enabled' : 'disabled'} successfully! Your changes will be deployed shortly.`);
      handleCloseMenuOnlyModal();
      
      setTimeout(() => {
        checkDeploymentStatus();
      }, 2000);
    } catch (err: any) {
      console.error('Error updating menu only mode:', err);
      onError(err.message || 'Failed to update menu only mode');
    } finally {
      setUpdatingMenuOnly(false);
    }
  };

  const handleIconFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      onError('Please select an image file for the app icon.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      onError('App icon file size must be less than 5MB.');
      return;
    }

    setUploadingIconFile(true);

    try {
      const storage = getStorage();
      const timestamp = Date.now();
      const path = `app-assets/${organizationId}/icons/${timestamp}-${file.name}`;
      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(snapshot.ref);
      
      setAppIconUrl(downloadUrl);
      setIconPreviewUrl(downloadUrl);
      onSuccess('App icon uploaded successfully!');
    } catch (error) {
      console.error('Error uploading app icon:', error);
      onError('Failed to upload app icon. Please try again.');
    } finally {
      setUploadingIconFile(false);
    }
  };

  // Parse and validate custom theme JSON
  const parseCustomTheme = (jsonText: string): { theme: Theme | null; error: string } => {
    try {
      const parsed = JSON.parse(jsonText);
      const themeData = parsed.customTheme || parsed;
      
      // Validate required fields
      const requiredFields = [
        'backgroundColor', 'textColor', 'cardColor', 'topbarBackgroundColor',
        'topbarIconColor', 'topbarBorderBottomColor', 'sidebarBackgroundColor',
        'sidebarIconColor', 'sidebarIconColorSelected', 'sidebarTextColor',
        'sidebarBorderColor', 'borderColor', 'categoryIndicatorColor',
        'cartBackgroundColor', 'cartCardColor', 'cartTextColor', 'cartButtonColor',
        'cartButtonTextColor', 'cartSummaryBorder', 'payButtonColor',
        'payButtonTextColor', 'loaderColor'
      ];
      
      for (const field of requiredFields) {
        if (!themeData[field]) {
          return { theme: null, error: `Missing required field: ${field}` };
        }
      }

      // Check total number of fields
      const totalFields = Object.keys(themeData).length;
      if (totalFields > 40) {
        return { theme: null, error: `Too many fields: ${totalFields}. Maximum is 40 fields.` };
      }
      
      return { theme: themeData as Theme, error: '' };
    } catch (error) {
      return { theme: null, error: 'Invalid JSON format. Please check the file and try again.' };
    }
  };

  const handleCustomThemeUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    processThemeFile(file);
  };

  const processThemeFile = (file: File) => {
    setCustomThemeError('');
    setCustomThemeFileName(file.name);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const { theme, error } = parseCustomTheme(content);
      
      if (theme) {
        setCustomTheme(theme);
        setCustomThemeError('');
      } else {
        setCustomTheme(null);
        setCustomThemeError(error);
      }
    };
    
    reader.readAsText(file);
  };

  const handleRemoveCustomTheme = () => {
    setCustomTheme(null);
    setCustomThemeError('');
    setCustomThemeFileName('');
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        processThemeFile(file);
      } else {
        setCustomThemeError('Please upload a JSON file.');
      }
    }
  };

  const handleRemoveIcon = () => {
    setAppIconUrl('');
    setIconPreviewUrl('');
  };

  const handleSubmitThemeUpdate = async () => {
    if (!appData?.id) return;

    const hasValidTheme = themeUpdateMode === 'default' || (themeUpdateMode === 'custom' && customTheme);

    if (!hasValidTheme) {
      onError('Please select a theme or upload a valid custom theme');
      return;
    }

    setUpdatingTheme(true);
    try {
      const retryDeployment = httpsCallable(functions, 'retryInstanceDeployment');
      
      const params: any = { appId: appData.id };

      if (themeUpdateMode === 'default') {
        params.theme = selectedTheme;
        params.themeAccentColor = accentColor;
      } else if (themeUpdateMode === 'custom' && customTheme) {
        params.customTheme = customTheme;
      }

      await retryDeployment(params);
      
      onSuccess('Theme update initiated successfully! Your changes will be deployed shortly.');
      handleCloseThemeModal();
      
      setTimeout(() => {
        checkDeploymentStatus();
      }, 2000);
    } catch (err: any) {
      console.error('Error updating theme:', err);
      onError(err.message || 'Failed to update theme');
    } finally {
      setUpdatingTheme(false);
    }
  };

  const handleSubmitIconUpdate = async () => {
    if (!appData?.id) return;

    if (!appIconUrl.trim()) {
      onError('Please provide an app icon URL');
      return;
    }

    setUpdatingIcon(true);
    try {
      const retryDeployment = httpsCallable(functions, 'retryInstanceDeployment');
      
      await retryDeployment({
        appId: appData.id,
        appIconUrl: appIconUrl.trim()
      });
      
      onSuccess('App icon update initiated successfully! Your changes will be deployed shortly.');
      handleCloseIconModal();
      
      setTimeout(() => {
        checkDeploymentStatus();
      }, 2000);
    } catch (err: any) {
      console.error('Error updating icon:', err);
      onError(err.message || 'Failed to update app icon');
    } finally {
      setUpdatingIcon(false);
    }
  };

  const getStatusColor = (status?: string) => {
    if (!status) return 'unknown';
    if (status === 'completed') return 'success';
    if (status === 'failed' || status === 'error') return 'error';
    if (status === 'in-progress' || status === 'pending') return 'progress';
    return 'unknown';
  };

  // Show store management if selected
  if (showStoreManagement) {
    return (
      <StoreManagement
        db={db}
        functions={functions}
        appData={appData}
        selectedOrganizationId={organizationId}
        onBack={handleBackFromStoreManagement}
        onBackToOrganizations={onBack}
        onError={onError}
        onSuccess={onSuccess}
        setLoading={setLoading}
      />
    );
  }

  return (
    <div className="control-panel">
      {/* Header */}
      <div className="control-panel-header">
        <button className="control-panel-back-btn" onClick={onBack}>
          ← Back to Sites
        </button>
        <h1>Control Panel: {appData.name}</h1>
      </div>

      {/* Content */}
      <div className="control-panel-content">

        {/* App Information Card */}
        <div className="control-panel-card">
          <div className="control-panel-card-header">
            <div className="control-panel-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <h4>App Information</h4>
          </div>
          
          <div className="control-panel-card-content">
            <div className="info-grid">
              <div className="info-item">
                <label>App ID:</label>
                <span>{appData.id}</span>
              </div>
              <div className="info-item">
                <label>Name:</label>
                <span>{appData.name}</span>
              </div>
              <div className="info-item">
                <label>Type:</label>
                <span>{appData.type || 'Unknown'}</span>
              </div>
              <div className="info-item">
                <label>Region:</label>
                <span>{appData.region || 'Unknown'}</span>
              </div>
              <div className="info-item">
                <label>Organization:</label>
                <span>{organizationId}</span>
              </div>
            </div>
          </div>
        </div>

                {/* Deployment Status Card */}
        {stores.length > 0 && <div className={`control-panel-card ${deploymentStatus?.error ? 'incomplete' : ''}`}>
          <div className="control-panel-card-header">
            <div className="control-panel-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <h4>Deployment Status</h4>
          </div>
          
          <div className="control-panel-card-content">
            {deploymentLoading ? (
              <div className="control-panel-loading">
                <div className="loading-spinner"></div>
                <p>Checking status...</p>
              </div>
            ) : deploymentStatus ? (
              <>
                {deploymentStatus.error ? (
                  <div className="deployment-error">
                    <span className="error-icon">❌</span>
                    <div className="error-details">
                      <p className="error-message">{deploymentStatus.error}</p>
                      {deploymentStatus.requestId && (
                        <p className="request-id">Request ID: {deploymentStatus.requestId}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="deployment-info">
                    <div className="deployment-status-row">
                      <label>Status:</label>
                      <span className={`deployment-status-badge ${getStatusColor(deploymentStatus.status)}`}>
                        {deploymentStatus.status || 'Unknown'}
                      </span>
                    </div>
                    
                    {deploymentStatus.currentStep && (
                      <div className="deployment-status-row">
                        <label>Current Step:</label>
                        <span>{deploymentStatus.currentStep}</span>
                      </div>
                    )}
                    
                    {deploymentStatus.details && (
                      <div className="deployment-status-row">
                        <label>Details:</label>
                        <span>{deploymentStatus.details}</span>
                      </div>
                    )}
                    
                    {deploymentStatus.url && (
                      <div className="deployment-status-row">
                        <label>URL:</label>
                        <a href={deploymentStatus.url} target="_blank" rel="noopener noreferrer" className="deployment-url">
                          {deploymentStatus.url}
                        </a>
                      </div>
                    )}
                    
                    {deploymentStatus.timestamp && (
                      <div className="deployment-status-row">
                        <label>Last Updated:</label>
                        <span>{new Date(deploymentStatus.timestamp).toLocaleString()}</span>
                      </div>
                    )}
                    
                    {deploymentStatus.requestId && (
                      <div className="deployment-status-row">
                        <label>Request ID:</label>
                        <span className="request-id-text">{deploymentStatus.requestId}</span>
                      </div>
                    )}
                    
                    {deploymentStatus.theme && (
                      <div className="deployment-status-row">
                        <label>Theme:</label>
                        <span>
                          {deploymentStatus.theme.hasCustomTheme ? (
                            <span className="custom-theme-badge">Custom Theme</span>
                          ) : (
                            <>
                              {deploymentStatus.theme.variant === 'modern-light' ? 'Light' : 'Dark'}
                              {deploymentStatus.theme.accentColor && (
                                <>
                                  {' • '}
                                  <span style={{ color: deploymentStatus.theme.accentColor }}>
                                    {deploymentStatus.theme.accentColor}
                                  </span>
                                </>
                              )}
                            </>
                          )}
                        </span>
                      </div>
                    )}
                    
                    {deploymentStatus.appIconUrl && (
                      <div className="deployment-status-row">
                        <label>App Icon:</label>
                        <a href={deploymentStatus.appIconUrl} target="_blank" rel="noopener noreferrer" className="deployment-url icon-url">
                          View Icon
                        </a>
                      </div>
                    )}

                    {/* ADDED - Menu Only Status Display */}
                    <div className="deployment-status-row">
                      <label>Menu Only Mode:</label>
                      <span className={`menu-only-badge ${deploymentStatus.menuOnly ? 'enabled' : 'disabled'}`}>
                        {deploymentStatus.menuOnly ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="deployment-actions">
                  <button 
                    type="button"
                    className="refresh-status-btn"
                    onClick={checkDeploymentStatus}
                    disabled={deploymentLoading}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10"/>
                      <polyline points="1 20 1 14 7 14"/>
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                    </svg>
                    Refresh Status
                  </button>
                  
                  {deploymentStatus.error && (
                    <button 
                      type="button"
                      className="retry-deployment-btn deployment-btn"
                      onClick={handleRetryDeployment}
                      disabled={retryingDeployment}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="23 4 23 10 17 10"/>
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                      </svg>
                      {retryingDeployment ? 'Retrying...' : 'Retry Deployment'}
                    </button>
                  )}

                  {deploymentStatus.updateAvailable && (
                    <button 
                      type="button"
                      className="deployment-btn update-deployment-btn"
                      onClick={handleRetryDeployment}
                      disabled={retryingDeployment}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="23 4 23 10 17 10"/>
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                      </svg>
                      {retryingDeployment ? 'Updating...' : 'Update Software'}
                    </button>
                  )}
                </div>
                
                {deploymentStatus.status === 'completed' && (
                  <div className="branding-update-actions">
                    <button 
                      type="button"
                      className="branding-update-btn"
                      onClick={handleOpenThemeModal}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="13.5" cy="6.5" r=".5"/>
                        <circle cx="17.5" cy="10.5" r=".5"/>
                        <circle cx="8.5" cy="7.5" r=".5"/>
                        <circle cx="6.5" cy="12.5" r=".5"/>
                        <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
                      </svg>
                      Update Theme
                    </button>
                    
                    <button 
                      type="button"
                      className="branding-update-btn"
                      onClick={handleOpenIconModal}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                      </svg>
                      Update App Icon
                    </button>

                    {/* ADDED - Menu Only Toggle Button */}
                    <button 
                      type="button"
                      className="branding-update-btn"
                      onClick={handleOpenMenuOnlyModal}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 12h18M3 6h18M3 18h18"/>
                      </svg>
                      Menu Only Mode?
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="no-deployment-status">
                <p>No deployment status available</p>
                <button 
                  type="button"
                  className="refresh-status-btn"
                  onClick={checkDeploymentStatus}
                  disabled={deploymentLoading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"/>
                    <polyline points="1 20 1 14 7 14"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                  Check Status
                </button>
              </div>
            )}
          </div>
        </div>}

        {/* Store Management Card */}
        <div className={`control-panel-card ${stores.length === 0 ? 'incomplete' : ''}`}>
          <div className="control-panel-card-header">
            <div className="control-panel-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9,22 9,12 15,12 15,22"/>
              </svg>
            </div>
            <h4>Store Management</h4>
            {!storesLoading && (
              <span className={`store-count-badge ${stores.length === 0 ? 'empty' : 'has-stores'}`}>
                {stores.length}
              </span>
            )}
          </div>
          
          <div className="control-panel-card-content">
            {storesLoading ? (
              <div className="control-panel-loading">
                <div className="loading-spinner"></div>
                <p>Loading stores...</p>
              </div>
            ) : stores.length === 0 ? (
              <div className="no-stores">
                <span className="warning-icon">⚠️</span>
                <p>No stores added yet. Your app needs at least one store to accept orders.</p>
              </div>
            ) : (
              <div className="stores-preview">
                {stores.slice(0, 3).map((store) => (
                  <div key={store.id} className="store-preview-item">
                    <div className="store-preview-info">
                      <h6>{store.name}</h6>
                      <p>{store.city}, {store.country}</p>
                    </div>
                    <div className="store-preview-status">
                      <span className={`status-dot ${store.stripeConnected ? 'connected' : 'pending'}`}></span>
                      <span className="status-text">
                        {store.stripeConnected ? 'Active' : 'Setup Pending'}
                      </span>
                    </div>
                  </div>
                ))}
                {stores.length > 3 && (
                  <div className="more-stores-indicator">
                    +{stores.length - 3} more store{stores.length - 3 > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            )}
            
            <div className="stores-actions">
              <button 
                type="button"
                className={`manage-stores-btn ${stores.length === 0 ? 'primary' : 'secondary'}`}
                onClick={handleManageStores}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {stores.length === 0 ? (
                    <>
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </>
                  ) : (
                    <>
                      <path d="M9 12l2 2 4-4"/>
                      <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"/>
                      <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"/>
                      <path d="M13 12h3a2 2 0 0 1 2 2v1"/>
                      <path d="M11 12H8a2 2 0 0 0-2 2v1"/>
                    </>
                  )}
                </svg>
                {stores.length === 0 ? 'Add Your First Store' : 'Manage Stores'}
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* Theme Update Modal */}
      {showThemeModal && (
        <div className="modal-overlay">
          <div className="modal-container theme-update-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Update Theme</h3>
                <button 
                  type="button" 
                  className="modal-close"
                  onClick={handleCloseThemeModal}
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body">
                <div className="theme-tabs">
                  <button
                    type="button"
                    className={`theme-tab ${themeUpdateMode === 'default' ? 'active' : ''}`}
                    onClick={() => setThemeUpdateMode('default')}
                  >
                    Default Theme
                  </button>
                  <button
                    type="button"
                    className={`theme-tab ${themeUpdateMode === 'custom' ? 'active' : ''}`}
                    onClick={() => setThemeUpdateMode('custom')}
                  >
                    Custom Theme
                  </button>
                </div>

                {themeUpdateMode === 'default' ? (
                  <div className="default-theme-content">
                    <div className="theme-selection">
                      <label>Theme Variant:</label>
                      <div className="theme-toggle">
                        <button
                          type="button"
                          className={`theme-toggle-btn ${selectedTheme === 'modern-light' ? 'active' : ''}`}
                          onClick={() => setSelectedTheme('modern-light')}
                        >
                          ☀️ Light
                        </button>
                        <button
                          type="button"
                          className={`theme-toggle-btn ${selectedTheme === 'modern-dark' ? 'active' : ''}`}
                          onClick={() => setSelectedTheme('modern-dark')}
                        >
                          🌙 Dark
                        </button>
                      </div>
                    </div>
                    
                    <div className="color-selection">
                      <label>Primary Accent Color:</label>
                      <div className="color-picker-section">
                        {/* <div className="color-preview" style={{ backgroundColor: accentColor }} /> */}
                        <div className="color-inputs">
                          <input
                            type="color"
                            value={accentColor}
                            onChange={(e) => setAccentColor(e.target.value)}
                            className="color-input"
                          />
                          <input
                            type="text"
                            value={accentColor}
                            onChange={(e) => setAccentColor(e.target.value)}
                            placeholder="#2563eb"
                            className="color-text"
                            pattern="^#[0-9A-Fa-f]{6}$"
                          />
                        </div>
                      </div>
                      <div className="color-presets">
                        {['#2563eb', '#7c3aed', '#dc2626', '#059669', '#ea580c', '#0891b2'].map((color) => (
                          <button
                            key={color}
                            type="button"
                            className="color-preset"
                            style={{ backgroundColor: color }}
                            onClick={() => setAccentColor(color)}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="custom-theme-content">
                    <div className="custom-theme-info">
                      <div className="info-box">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px', flexShrink: 0}}>
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M12 16v-4"/>
                          <path d="M12 8h.01"/>
                        </svg>
                        <div>
                          <p><strong>Need to create a custom theme?</strong></p>
                          {deploymentStatus?.url ? (
                            <p>Visit <a href={`${deploymentStatus.url}/theme-customizer`} target="_blank" rel="noopener noreferrer" className="theme-link">{deploymentStatus.url}/theme-customizer</a> to create and download a custom theme JSON file.</p>
                          ) : (
                            <p>Visit your app's theme customizer (URL/theme-customizer) to create and download a custom theme JSON file.</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {!customTheme ? (
                      <div className="file-upload-section">
                        <label className="file-upload-label">
                          <input
                            type="file"
                            accept=".json"
                            onChange={handleCustomThemeUpload}
                            className="file-upload-input"
                          />
                          <div 
                            className="file-upload-area"
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                              <polyline points="17 8 12 3 7 8"/>
                              <line x1="12" y1="3" x2="12" y2="15"/>
                            </svg>
                            <p>Click to upload theme JSON file</p>
                            <small>or drag and drop your theme.json file here</small>
                          </div>
                        </label>

                        {customThemeError && (
                          <div className="config-error">
                            <span className="error-icon">❌</span>
                            {customThemeError}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="custom-theme-loaded">
                        <div className="theme-file-info">
                          <div className="file-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                              <polyline points="14,2 14,8 20,8"/>
                              <line x1="16" y1="13" x2="8" y2="13"/>
                              <line x1="16" y1="17" x2="8" y2="17"/>
                              <polyline points="10,9 9,9 8,9"/>
                            </svg>
                          </div>
                          <div className="file-details">
                            <p className="file-name">{customThemeFileName}</p>
                            <p className="file-status">✅ Custom theme loaded</p>
                          </div>
                          <button
                            type="button"
                            className="remove-theme-btn"
                            onClick={handleRemoveCustomTheme}
                            title="Remove custom theme"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                              <line x1="10" y1="11" x2="10" y2="17"/>
                              <line x1="14" y1="11" x2="14" y2="17"/>
                            </svg>
                          </button>
                        </div>

                        <div className="theme-preview-grid">
                          <div className="preview-item">
                            <span className="preview-label">Background:</span>
                            <div className="color-swatch" style={{ backgroundColor: customTheme.backgroundColor }}></div>
                            <span className="color-value">{customTheme.backgroundColor}</span>
                          </div>
                          <div className="preview-item">
                            <span className="preview-label">Text:</span>
                            <div className="color-swatch" style={{ backgroundColor: customTheme.textColor }}></div>
                            <span className="color-value">{customTheme.textColor}</span>
                          </div>
                          <div className="preview-item">
                            <span className="preview-label">Card:</span>
                            <div className="color-swatch" style={{ backgroundColor: customTheme.cardColor }}></div>
                            <span className="color-value">{customTheme.cardColor}</span>
                          </div>
                          <div className="preview-item">
                            <span className="preview-label">Topbar:</span>
                            <div className="color-swatch" style={{ backgroundColor: customTheme.topbarBackgroundColor }}></div>
                            <span className="color-value">{customTheme.topbarBackgroundColor}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="update-note">
                  <p><strong>Note:</strong> Updating the theme will trigger a redeployment of your app. This may take a few minutes to complete.</p>
                </div>
              </div>
              
              <div className="modal-footer">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCloseThemeModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="submit-update-btn"
                  onClick={handleSubmitThemeUpdate}
                  disabled={updatingTheme || (themeUpdateMode === 'custom' && !customTheme)}
                >
                  {updatingTheme ? (
                    <>
                      <div className="spinner" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      Apply Theme
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Icon Update Modal */}
      {showIconModal && (
        <div className="modal-overlay">
          <div className="modal-container icon-update-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Update App Icon</h3>
                <button 
                  type="button" 
                  className="modal-close"
                  onClick={handleCloseIconModal}
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body">
                <div className="icon-upload-section">
                  <label>App Icon:</label>
                  
                  <div className="file-upload-area">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleIconFileUpload}
                      className="file-input"
                      disabled={uploadingIconFile}
                      id="icon-file-input"
                    />
                    
                    <div className="upload-controls">
                      <button
                        type="button"
                        className="upload-button"
                        onClick={() => document.getElementById('icon-file-input')?.click()}
                        disabled={uploadingIconFile}
                      >
                        {uploadingIconFile ? (
                          <>
                            <div className="spinner" />
                            Uploading...
                          </>
                        ) : (
                          'Choose File'
                        )}
                      </button>
                      
                      <input
                        type="url"
                        value={appIconUrl}
                        onChange={(e) => {
                          setAppIconUrl(e.target.value);
                          setIconPreviewUrl(e.target.value);
                        }}
                        placeholder="Or paste image URL"
                        className="url-input"
                        disabled={uploadingIconFile}
                      />
                    </div>
                  </div>
                  
                  {iconPreviewUrl && (
                    <div className="image-preview-container">
                      <div className="image-preview">
                        <img 
                          src={iconPreviewUrl} 
                          alt="App icon preview" 
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }} 
                        />
                      </div>
                      <button
                        type="button"
                        className="remove-image"
                        onClick={handleRemoveIcon}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  
                  <small className="input-hint">Square format recommended (512x512px minimum)</small>
                </div>

                <div className="update-note">
                  <p><strong>Note:</strong> Updating the app icon will trigger a redeployment of your app. This may take a few minutes to complete.</p>
                </div>
              </div>
              
              <div className="modal-footer">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCloseIconModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="submit-update-btn"
                  onClick={handleSubmitIconUpdate}
                  disabled={updatingIcon || !appIconUrl.trim()}
                >
                  {updatingIcon ? (
                    <>
                      <div className="spinner" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      Apply Icon
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ADDED - Menu Only Update Modal */}
      {showMenuOnlyModal && (
        <div className="modal-overlay">
          <div className="modal-container menu-only-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Toggle Menu Only Mode</h3>
                <button 
                  type="button" 
                  className="modal-close"
                  onClick={handleCloseMenuOnlyModal}
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body">
                <div className="menu-only-info">
                  <div className="info-box">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px', flexShrink: 0}}>
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 16v-4"/>
                      <path d="M12 8h.01"/>
                    </svg>
                    <div>
                      <p><strong>What is Menu Only Mode?</strong></p>
                      <p>When enabled, your app will display only the menu without ordering functionality.</p>
                    </div>
                  </div>
                </div>

                <div className="menu-only-toggle-section">
                  <label className="toggle-label">
                    <span>Menu Only Mode:</span>
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={menuOnlyValue}
                        onChange={(e) => setMenuOnlyValue(e.target.checked)}
                        className="toggle-input"
                      />
                      <span className="toggle-slider"></span>
                    </div>
                    <span className={`toggle-status ${menuOnlyValue ? 'enabled' : 'disabled'}`}>
                      {menuOnlyValue ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                </div>

                <div className="update-note">
                  <p><strong>Note:</strong> Toggling menu only mode will trigger a redeployment of your app. This may take a few minutes to complete.</p>
                </div>
              </div>
              
              <div className="modal-footer">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCloseMenuOnlyModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="submit-update-btn"
                  onClick={handleSubmitMenuOnlyUpdate}
                  disabled={updatingMenuOnly}
                >
                  {updatingMenuOnly ? (
                    <>
                      <div className="spinner" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      Apply Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ControlPanel;